import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faComments, faChalkboardTeacher, faUserFriends } from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as CommonFileTextClock } from "@app/assets/svgs/common-file-text-clock.svg";
import { ReactComponent as ELearningMonitor } from "@app/assets/svgs/e-learning-monitor.svg";
import { ReactComponent as Calendar3 } from "@app/assets/svgs/calendar-3.svg";
import { ReactComponent as FolderStar } from "@app/assets/svgs/folder-star.svg";
import { ReactComponent as GroupIcon } from "@app/assets/svgs/multiple-neutral-2.svg";
import StreamlineIconWrapper from "./components/StreamlineIconWrapper";

const idArrayToDict = (arr) => arr.reduce((res, acc) => { res[acc.id] = acc; return res; }, {});

export const SECOND = 1000;
export const MINUTE = SECOND * 60;

export const FEATURE_FLAGS_LOADING_TOLERANCE = MINUTE * 4;

export const NEW_VERSION_CHECK_INTERVAL = MINUTE * 5;
export const MAX_PROCESS_REPORT_PERIOD_MONTHS = 6;

export const SESSION_TIMEOUT = MINUTE * 120;
export const SESSION_TIMEOUT_STUDENTS = MINUTE * 120;

export const IS_PRD = window.location.hostname === "clinic.poppins.co.il";
export const IS_DEV_OR_STG = window.location.href.includes("poppins.live");
export const IS_DEV = window.location.href.includes("localhost");

export const ROLES = {
  UNVERIFIED: { id: 0, title: "Unverified", HE_title: "לא נרשם" },
  STUDENT: { id: 1, title: "Student", HE_title: "מטופל" },
  TRAINER: { id: 10, title: "Practice Buddy", HE_title: "חבר ללמידה" },
  THERAPIST: { id: 20, title: "Interventionist", HE_title: "מטפל" },
  CLINIC_ADMINISTRATOR: { id: 50, title: "School Administrator", HE_title: "מנהל קופה" },
  CLINICAL_SUPERVISOR: { id: 40, title: "Clinical Supervisor", HE_title: "מנהל מחוז" },
  SPED: { id: 65, title: "District Supervisor", HE_title: "מנהל לקוח" },
  TECH_SUPPORT: { id: 90, title: "Tech Support", HE_title: "תמיכה טכנית" },
  SYSTEM_ADMIN: { id: 100, title: "System Admin", HE_title: "מנהל מערכת" },
};

export const CONSENTS = {
  NOT_GIVEN_CONSENT: { id: 0, title: "Not Given" },
  GOOD_CONSENT: { id: 1, title: "Good" },
  BLOCKED_CONSENT: { id: 2, title: "Blocked" },
  NO_CONSENTED_STATUS: 0,
  CONSENTED_STATUS: 1,
};

export const TREATMENT_STATUSES = {
  SCHEDULED: { id: 0, title: "Scheduled" },
  DONE: { id: 1, title: "Done" },
  MISSED: { id: 2, title: "Missed" },
  CANCELLED: { id: 3, title: "Cancelled" },
};

export const TREATMENT_TYPES = {
  THERAPY: { id: 1, title: "Supervised session", defaultDuration: 30, isDirect: true, visibleForTherapist: true, isNew: false, isOnSite: false, icon: <FontAwesomeIcon icon={faComments} className="icon-type icon-type-therapy" />, treatmentPath: "treatment" },
  PRACTICE: { id: 2, title: "Practice", defaultDuration: 10, isDirect: true, visibleForTherapist: false, isNew: false, isOnSite: false, icon: <FontAwesomeIcon icon={faUserFriends} className="icon-type icon-type-practice" />, treatmentPath: "treatment" }, // LEGACY - not in use
  SELF_PRACTICE: { id: 3, title: "Self practice", defaultDuration: 10, isDirect: false, treatmentPath: "treatment" }, // LEGACY - not in use
  GROUP: { id: 7, title: "Group session", defaultDuration: 30, isDirect: true, visibleForTherapist: false, isNew: false, isOnSite: false, icon: <FontAwesomeIcon icon={faUsers} className="icon-type icon-type-group" />, treatmentPath: "treatment" },
  CLASS: { id: 8, title: "Session", defaultDuration: 30, isDirect: true, visibleForTherapist: true, isNew: false, isOnSite: true, icon: <FontAwesomeIcon icon={faChalkboardTeacher} className="icon-type icon-type-class" />, treatmentPath: "treatment/v3" },
  V4_SESSION_PILOT: { id: 9, title: "Session", defaultDuration: 30, isDirect: true, visibleForTherapist: true, isNew: false, isOnSite: true, icon: <FontAwesomeIcon icon={faChalkboardTeacher} className="icon-type icon-type-class" />, treatmentPath: "treatment/v4" },
};

export const TREATMENT_TYPES_IDS = idArrayToDict(Object.values(TREATMENT_TYPES));

export const STUDENT_SESSION_STATUS = {
  10: { title: "Not Registered", text: "User has't logged in to the system yet", isRed: true },
  20: { title: "Registration Expired", text: "User still hasn't logged in, registration notification was sent over 7 days ago", isRed: true },
  30: { title: "Waiting", text: "No future scheduled sessions", isRed: true },
  40: { title: "Scheduled Sessions", text: "At least one future session is scheduled, no 'completed' sessions yet", isRed: false },
  50: { title: "Scheduled Sessions But Not Registered", text: "User hasn't logged in and there is at least one future session scheduled", isRed: false },
  60: { title: "Completed Sessions", text: "At least one 'completed' session or 'pending' session", isRed: false },
};

export const TREATMENT_TYPES_LANG_DICT = {
  1: {
    1: "individual",
    2: "practice",
    4: "assessment",
    7: "group",
    8: "class",
  },
  2: {
    1: "individual",
    2: "practice",
    4: "assessment",
    7: "group",
    8: "class",
  },
  3: {
    1: "individual session",
    2: "practice",
    4: "assessment",
    7: "group session",
    8: "class",
  },
};

export const TREATMENT_TYPES_LABELS = Object.values(TREATMENT_TYPES).reduce((res, item) => {
  res[item.id] = item.title;
  return res;
}, {});

export const USER_GENDERS = {
  NOT_SPECIFIED_GENDER: { id: 0, title: "Not specified" },
  MALE_GENDER: { id: 1, title: "Male" },
  FEMALE_GENDER: { id: 2, title: "Female" },
  OTHER_GENDER: { id: 3, title: "Other" },
};

export const TREATMENT_DURATION_MINUTES = {
  STANDART_TREATMENT_DURATION: 30,
  DYSLEXIA_TREATMENT_DURATION: 45,
  ASSESMENT_TREATMENT_DURATION: 90,
};

export const API_CACHE_TIME = 600; // 10 minutes
export const DATETIME_FORMAT = "MMM Do YYYY, hh:mm a";
export const DATETIME_UTC_FORMAT = "YYYY-MM-DD HH:mm:ss.SSS";
export const DATE_FORMAT = "MM/DD";
export const DATE_PERIOD_FORMAT = "YY-MMM-DD";
// For use with the date selector
export const DT_FORMAT = "YYYY-MM-DDTHH:mm";
// Prefix for treatment webSocket
export const TREATMENT_WS_PREFIX = "TREATMENT_WS_PREFIX";

export const GoogleAnalyticsID = "UA-149718238-1";
export const GoogleAnalyticsStagingID = "UA-149718238-2";

// MIXPANEL
export const MIXPANEL_PROD_TOKEN = "546598c36d05a649722bf0359ad4f2fe";
export const MIXPANEL_STG_TOKEN = "305384c1569f06d1e17ffc408935134c";

// SERVICE
export const SERVICE_DYSLEXIA_DB_NAME = "dyslexia";
// export const SERVICES = {
//   SPEECH_LANGUAGE: 1,
//   DYSLEXIA: 2,
// };


export const SERVICES = {
  SPEECH_LANGUAGE: { id: 1, title: "Speech/Language Therapy (SL)", visibleForTherapist: true, isNew: false },
  OCCUPATIONAL_THERAPY: { id: 3, title: "Occupational Therapy (OT)", visibleForTherapist: false, isNew: false },
};

export const SERVICES_IDS = idArrayToDict(Object.values(SERVICES));

// Dyslexia attribution program
export const DYSLEXIA_ATTRIBUTION = {
  SPECIAL_EDUCATION: "sped",
  504: "504",
  PRE_ASSESSMENT: "preAssessment",
};

// Treatment ping interval
export const TreatmentPingMSInterval = 7000;

// COURSES
export const DYSLEXIA_COURSE_ID = 5;

// WebSocket Error for authentication
export const WS_ERROR_CODES = {
  WS_UNAUTH: 3401,
  WS_PERMISSION_DENIED: 3403,
  WS_DUPLICATE: 3409,
  WS_GAIERR_CODE: 3503, //It is not 5003 or 5000 because the server didnt return thouse close codes
  WS_UNKNOWN_EXCEPTION_CODE: 3500,
};

export const DATEONLY_FORMAT = "MMM d, yyyy";

export const AMPLIO_ANALITICS_REQUEST_TYPES = {
  WORD_SUMMARY: "word_summary",
  TIME_SERIES: "time_series",
};

export const ROUTES = {
  CLASSROOM: "/treatment/v3", // BACKWARDS COMPATABILITY
  CLASSROOM_V3: "/treatment/v3",
  CLASSROOM_V4: "/treatment/v4",
  STUDENTS: "/home/student-groups",
  CALENDAR: "/home/calendar",
  TIMESHEET: "/home/reports",
  RESOURCES: "/home/resources",
};

export const TABS = {
  STUDENTS: "student-groups",
  CALENDAR: "calendar",
  TIMESHEET: "reports",
  RESOURCES: "resources",
};

export const THERAPIST_NAVIGATION_ITEMS = [
  { path: ROUTES.CLASSROOM_V3, name: "Classroom", icon: <StreamlineIconWrapper><ELearningMonitor /></StreamlineIconWrapper> },
  { path: ROUTES.CLASSROOM_V4, name: "Classroom", icon: <StreamlineIconWrapper><ELearningMonitor /></StreamlineIconWrapper> },
  { path: ROUTES.STUDENTS, name: "Students and Groups", icon: <StreamlineIconWrapper><GroupIcon /></StreamlineIconWrapper> },
  { path: ROUTES.CALENDAR, name: "Calendar", icon: <StreamlineIconWrapper><Calendar3 /></StreamlineIconWrapper> },
  { path: ROUTES.TIMESHEET, name: "Timesheet", icon: <StreamlineIconWrapper><CommonFileTextClock /></StreamlineIconWrapper> },
  { path: ROUTES.RESOURCES, name: "Resources", icon: <StreamlineIconWrapper><FolderStar /></StreamlineIconWrapper> },
];

export const recurrenceDays = [
  { id: 1, label: "Monday", initial: "M" },
  { id: 2, label: "Tuesday", initial: "T" },
  { id: 3, label: "Wednesday", initial: "W" },
  { id: 4, label: "Thursday", initial: "T" },
  { id: 5, label: "Friday", initial: "F" },
  { id: 6, label: "Saturday", initial: "S" },
  { id: 0, label: "Sunday", initial: "S" },
];

export const recurrenceTypes = {
  DAILY: 1,
  WEEKLY: 2,
  MONTHLY: 3,
};

export const monthlyRecurrence = {
  DAY_OF_MONTH: "day_of_month",
  WEEK_OF_MONTH: "week_of_month",
};

export const TYPE_STUDENT = 1;
export const TYPE_THERAPIST = 2;
export const TYPE_SCHOOL = 3;

export const NEW_VERSION_MSG = "A newer version is available";
export const TOAST_ID_VER_CHK = "no-duplicate-version-check";

//Vocabulary analytics
export const ARTICULATION_SOUNDS = "articulation_sounds";
export const ARTICULATION_SOUNDS_POSITIONS = "articulation_sounds_positions";
export const VOCABULARY_CATEGORIES = "vocabulary_categories";
export const NUM_OF_SYLLABLES = "num_of_syllables";
export const SYLLABLES_SHAPES = "syllables_shapes";
export const THEMES = "themes";
export const GRADES = "grades";

export const MEASUREMENT_TYPES = {
  ARTICULATION: 1,
  DEFINITION_MATCHING: 2,
  FLUENCY: 3,
  SPEECH: 4, // TYPETOKENRATION TTR
  WCPM: 5,
  GENERAL: 6,
  READING: 7,
  NAMING: 8,
  SPELLING: 9,
  CUE: 10,
};

export const VIDEO_SESSION_PAGES = {
  SESSION_START: "SESSION_START",
  VIDEO: "VIDEO",
  SCREENSHARE: "SCREENSHARE",
  ACTIVITY_LEGACY: "ACTIVITY_LEGACY",
  ACTIVITY: "ACTIVITY",
  TOOLS: "TOOLS",
  TEST: "TEST",
  YOUTUBE: "YouTube",
  WHITEBOARD: "Whiteboard",
  GAME: "Game",
  SESSION_END: "SESSION_END",
  VOCABULARY: "VOCABULARY",
};

export const TECH_INDICATIONS = {
  DEVICE_SUPPORT: "error_tech_assist_device_support",
  OS_SUPPORT: "error_tech_assist_os_support",
  BROWSER_SUPPORT: "error_tech_assist_browser_support",
  OS_VERSION_SUPPORT: "error_tech_assist_os_version_support",
  BROWSER_VERSION_SUPPORT: "error_tech_assist_browser_version_support",
  INTERNET_CONNECTION: "error_tech_assist_internet_connection",
  CPU: "error_tech_assist_cpu",
  PERMISSIONS: "error_tech_assist_permissions",
  USER_FOCUS: "error_tech_assist_user_focus",
  OPENTOK_STREAM: "error_tech_assist_opentok_stream",
};

// Tech Assist
export const OS_IOS = "OS_IOS";
export const OS_ANDROID = "OS_ANDROID";
export const OS_WINDOWS = "OS_WINDOWS";
export const OS_WINDOWS_PHONE = "OS_WINDOWS_PHONE";
export const OS_MAC_OS = "OS_MAC_OS";

export const DEVICE_MOBILE = "MOBILE";
export const DEVICE_TABLET = "DEVICE_TABLET";
export const DEVICE_SMARTTV = "DEVICE_SMARTTV";
export const DEVICE_CONSOLE = "DEVICE_CONSOLE";
export const DEVICE_WEARBLE = "DEVICE_WEARBLE";
export const DEVICE_EMBEDDED = "DEVICE_EMBEDDED";
export const DEVICE_BROWSER = "DEVICE_BROWSER";

export const BROWSER_CHROME = "BROWSER_CHROME";
export const BROWSER_FIREFOX = "BROWSER_FIREFOX";
export const BROWSER_OPERA = "BROWSER_OPERA";
export const BROWSER_SAFARI = "BROWSER_SAFARI";
export const BROWSER_YANDEX = "BROWSER_YANDEX";
export const BROWSER_EXPLORER = "BROWSER_EXPLORER";
export const BROWSER_CHROMIUM = "BROWSER_CHROMIUM";
export const BROWSER_EDGE = "BROWSER_EDGE";
export const BROWSER_IE = "BROWSER_IE";
export const BROWSER_MOBILE_SAFARI = "BROWSER_MOBILE_SAFARI";
export const BROWSER_EDGE_CHROMIUM = "BROWSER_EDGE_CHROMIUM";
export const BROWSER_MIUI = "BROWSER_MIUI";
export const BROWSER_SAMSUNG = "BROWSER_SAMSUNG";

//react-device-detect translation
export const OS_NAME_DICT = {
  iOS: OS_IOS,
  Android: OS_ANDROID,
  "Windows Phone": OS_WINDOWS_PHONE,
  Windows: OS_WINDOWS,
  "Mac OS": OS_MAC_OS,
};

export const DEVICE_TYPE_DICT = {
  mobile: DEVICE_MOBILE,
  tablet: DEVICE_TABLET,
  smarttv: DEVICE_SMARTTV,
  console: DEVICE_CONSOLE,
  wearable: DEVICE_WEARBLE,
  embedded: DEVICE_EMBEDDED,
  browser: DEVICE_BROWSER,
};

export const BROWSER_TYPE_DICT = {
  Chrome: BROWSER_CHROME,
  Firefox: BROWSER_FIREFOX,
  Opera: BROWSER_OPERA,
  Yandex: BROWSER_YANDEX,
  Safari: BROWSER_SAFARI,
  "Internet Explorer": BROWSER_EXPLORER,
  Edge: BROWSER_EDGE,
  Chromium: BROWSER_CHROMIUM,
  IE: BROWSER_IE,
  "Mobile Safari": BROWSER_MOBILE_SAFARI,
  "Edge Chromium": BROWSER_EDGE_CHROMIUM,
  "MIUI Browser": BROWSER_MIUI,
  "Samsung Browser": BROWSER_SAMSUNG,
};

export const MEASUREMENT_WIDGETS_TYPES = {
  TITLE_WIDGET: "TitleWidget",
  IMAGE_WIDGET: "ImageWidget",
  CARD_WIDGET: "CardWidget",
  DECK_WIDGET: "DeckWidget",
  DICE_WIDGET: "DiceWidget",
  LETTER_ORDER_WIDGET: "LetterOrderWidget",
  CV_ORDER_WIDGET: "CVOrderWidget",
  TEXT_WIDGET: "TextWidget",
  SMART_TEXT_WIDGET: "SmartTextWidget",
  SPELLING_CHECK_WIDGET: "SpellingCheckWidget",
  SPELLING_DECK_WIDGET: "SpellingDeckWidget",
  SOUND_IDENTIFICATION_WIDGET: "SoundIdentificationWidget",
  MATCHIN_SORTING_WIDGET: "MatchinSorting",
  SYLLABIFICATION_WIDGET: "SyllabificationWidget",
  FILL_IN_WIDGET: "FillIn",

  CLONEAD_IMAGE: "CloneadImage",
};

export const TREATMENT_VERSION = {
  V3: 3,
  V4: 4,
};
